// store/setupApi.js
import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "./baseQuery";

/** Normalize backend keys (hyphenated) -> camelCase the UI expects */
const normalizeSetup = (res = {}) => ({
  id: res._id ?? null,
  generalSettings: res["general-settings"] ?? {},
  settings: res.settings ?? {},
  theme: res.theme ?? {},
  fonts: res.fonts ?? {},
  colors: res.colors ?? {},
  exceptions: Array.isArray(res.exceptions) ? res.exceptions : [],
  openingHours: {
    lunch: res["openinghours-lunch"] ?? null,
    dinner: res["openinghours-dinner"] ?? null,
    breakfast: res["openinghours-breakfast"] ?? null,
  },
  eventCards: res["event-cards"] ?? {},
  // Include restaurant page data if present in response
  restaurantPage: res["restaurant-page"] ?? null,
  accountType: res.account_type ?? null,
  yeatRestaurantId: res.yeatRestaurantId ?? null,
});

export const setupApi = createApi({
  reducerPath: "setupApi",
  baseQuery: dynamicBaseQuery,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  keepUnusedDataFor: 300, // seconds
  tagTypes: ["OpeningHours", "Exceptions", "Settings", "Theme", "RestaurantSetup"],
  endpoints: (builder) => ({
    /** Single source of truth for restaurant setup.
     *  IMPORTANT: Treat 404 as "no data yet" and return a normalized empty payload,
     *  so RTK Query caches it and doesn't refetch endlessly or flash the UI.
     */
    getRestaurantSetup: builder.query({
      /**
       * Use queryFn to intercept 404s and convert them into cached empty data.
       */
      async queryFn(_arg, _api, _extraOptions, baseQuery) {
        const res = await baseQuery({
          url: "api/auth-restaurant",
          method: "GET",
          headers: { "Cache-Control": "no-cache" },
        });

        if (res.error) {
          const status =
            res.error?.status ??
            res.error?.originalStatus ??
            res.error?.data?.status ??
            res.error?.data?.statusCode;

          if (Number(status) === 404) {
            // Cache an empty normalized setup to avoid loops/flashing
            return { data: normalizeSetup({}) };
          }
          // Propagate other errors
          return { error: res.error };
        }

        return { data: normalizeSetup(res.data) };
      },

      // Provide tags for invalidation from mutations elsewhere
      providesTags: [
        "RestaurantSetup",
        { type: "OpeningHours", id: "LIST" },
        { type: "Exceptions", id: "LIST" },
        { type: "Settings", id: "LIST" },
        { type: "Theme", id: "LIST" },
      ],

      // Never force refetch if args are unchanged
      forceRefetch({ currentArg, previousArg }) {
        return false;
      },
    }),

    /** Update opening hours for a specific meal type */
    updateOpeningHours: builder.mutation({
      query: ({ mealType, ...body }) => ({
        url: `api/openinghours-${mealType}`,
        method: "POST",
        body,
      }),
      // Invalidate the restaurant setup cache to trigger a refetch
      invalidatesTags: [
        "RestaurantSetup",
        { type: "OpeningHours", id: "LIST" },
        { type: "Settings", id: "LIST" },
      ],
    }),

    /** Create exception */
    createException: builder.mutation({
      query: (body) => ({
        url: "api/exceptions",
        method: "POST",
        body,
      }),
      invalidatesTags: ["RestaurantSetup", { type: "Exceptions", id: "LIST" }],
    }),

    /** Update exception */
    updateException: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `api/exceptions/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["RestaurantSetup", { type: "Exceptions", id: "LIST" }],
    }),

    /** Delete exception */
    deleteException: builder.mutation({
      query: (id) => ({
        url: `api/exceptions/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["RestaurantSetup", { type: "Exceptions", id: "LIST" }],
    }),

  }),
});

export const {
  useGetRestaurantSetupQuery,
  usePrefetch,
  useUpdateOpeningHoursMutation,
  useCreateExceptionMutation,
  useUpdateExceptionMutation,
  useDeleteExceptionMutation,
} = setupApi;

/* Convenience selector helpers */
export const selectRestaurantSetup =
  setupApi.endpoints.getRestaurantSetup.select();

export const selectOpeningHoursLunch = (state) =>
  selectRestaurantSetup(state)?.data?.openingHours?.lunch ?? null;

export const selectOpeningHoursDinner = (state) =>
  selectRestaurantSetup(state)?.data?.openingHours?.dinner ?? null;

export const selectOpeningHoursBreakfast = (state) =>
  selectRestaurantSetup(state)?.data?.openingHours?.breakfast ?? null;

export const selectExceptions = (state) =>
  selectRestaurantSetup(state)?.data?.exceptions ?? [];

export const selectSettings = (state) =>
  selectRestaurantSetup(state)?.data?.settings ?? {};

export const selectGeneralSettings = (state) =>
  selectRestaurantSetup(state)?.data?.generalSettings ?? {};

export const selectTheme = (state) =>
  selectRestaurantSetup(state)?.data?.theme ?? {};

export const selectFonts = (state) =>
  selectRestaurantSetup(state)?.data?.fonts ?? {};

export const selectColors = (state) =>
  selectRestaurantSetup(state)?.data?.colors ?? {};

export const selectRestaurantPage = (state) =>
  selectRestaurantSetup(state)?.data?.restaurantPage ?? null;

export const selectEventCards = (state) =>
  selectRestaurantSetup(state)?.data?.eventCards ?? {};

export const selectIsBowlingRestaurant = (state) => {
  const data = selectRestaurantSetup(state)?.data;
  // Check both locations: root level accountType and inside generalSettings
  return data?.accountType === 'bowling' || data?.generalSettings?.account_type === 'bowling';
};

export const selectEmailInboxEnabled = (state) =>
  selectRestaurantSetup(state)?.data?.generalSettings?.emailInboxEnabled === true;

export const selectNewsletterEnabled = (state) =>
  selectRestaurantSetup(state)?.data?.generalSettings?.newsletter_enabled === true;

export const selectNoYeatId = (state) =>
  !selectRestaurantSetup(state)?.data?.yeatRestaurantId;
